/**
 * @author Michael Hasenstein <hasenstein@yahoo.com>
 * @copyright REFINIO GmbH 2017
 * @license CC-BY-NC-SA-2.5; portions MIT License
 * @version 0.0.1
 */
import { createError } from './errors.js';
import { SET_ACCESS_MODE } from './storage-base-common.js';
import { getObjectByIdObj, storeVersionedObject } from './storage-versioned-objects.js';
import { isHash } from './util/type-checks.js';
/**
 * @private
 * @param {SetAccessParam} accessRequest
 * @returns {boolean} Returns `true` if the given object is a {@link SetAccessParam} object
 * @throws {Error} If there are missing oir conflicting parameters an `Error` is thrown
 */
function isSetAccessParam(accessRequest) {
    if (accessRequest.mode !== SET_ACCESS_MODE.ADD &&
        accessRequest.mode !== SET_ACCESS_MODE.REPLACE) {
        return false;
    }
    if (accessRequest.object === undefined && accessRequest.id === undefined) {
        return false;
    }
    if (accessRequest.object !== undefined && accessRequest.id !== undefined) {
        return false;
    }
    if (accessRequest.object !== undefined && !isHash(accessRequest.object)) {
        return false;
    }
    if (accessRequest.id !== undefined && !isHash(accessRequest.id)) {
        return false;
    }
    // NOTE: An empty array is allowed! This creates an Access object without any grants, which
    // revokes any grants set in the previous version of the Access object. We place no
    // restriction on the `mode`, so it is possible to use mode="add" and an empty array to
    // create a new Access object that is exactly the same as the last one. Whether this writes
    // anything to storage - it would only affect the version map - depends on the version map
    // policy (`storeVersionedObject` parameter).
    if (accessRequest.person !== undefined &&
        (!Array.isArray(accessRequest.person) || !accessRequest.person.every(item => isHash(item)))) {
        return false;
    }
    // noinspection RedundantIfStatementJS
    if (accessRequest.hashGroup !== undefined &&
        (!Array.isArray(accessRequest.hashGroup) || !accessRequest.hashGroup.every(item => isHash(item)))) {
        return false;
    }
    return true;
}
/**
 * The function creates an Access object or an IdAccess object
 * @private
 * @param {SetAccessParam} accessRequest
 * @returns {Promise<VersionedObjectResult>}
 * @throws {Error} If there are missing oir conflicting parameters an `Error` is thrown
 */
async function setAccessForOneObject(accessRequest) {
    if (!isSetAccessParam(accessRequest)) {
        throw createError('ACC-CP1', { accessRequest });
    }
    const accessObj = accessRequest.id === undefined
        ? {
            $type$: 'Access',
            object: accessRequest.object,
            person: accessRequest.person ?? [],
            hashGroup: accessRequest.hashGroup ?? []
        }
        : {
            $type$: 'IdAccess',
            id: accessRequest.id,
            person: [...new Set(accessRequest.person ?? [])],
            hashGroup: [...new Set(accessRequest.hashGroup ?? [])]
        };
    // REPLACE or ADD
    if (accessRequest.mode === SET_ACCESS_MODE.ADD) {
        try {
            const { obj: previousAccessObj } = await getObjectByIdObj(accessObj);
            accessObj.person.push(...previousAccessObj.person);
            accessObj.hashGroup.push(...previousAccessObj.hashGroup);
        }
        catch (err) {
            // "File not found" is okay, there may not be a previous version
            if (err.name !== 'FileNotFoundError') {
                throw err;
            }
        }
    }
    return await storeVersionedObject({
        ...accessObj,
        person: [...new Set(accessObj.person)],
        hashGroup: [...new Set(accessObj.hashGroup)]
    });
}
/**
 * Create one Access object for a given versioned ONE object.  Access objects have "references"
 * and not just a simple SHA-256 hash for linkage to the target object because objects should be
 * linked through reference links, but more importantly, only Reference object links trigger the
 * writing of reverse maps, which makes it possible to start from a given Person and find all
 * Access objects that reference it. For any given versioned object find all Access objects
 * referencing it (granting access to someone).
 * If the `person` or `group` array is empty an Access object without grants is created if no
 * previous Access object existed for the given object. If there already is one and `mode` is
 * `mode` is "add" (constant `Storage.SET_ACCESS_MODE.ADD`) only a new version map entry is
 * created, if `mode` is "replace" a new most current Access object version is created without
 * grants, thereby revoking any access rights that may previously have existed.
 *
 * @example
 *
 * // Set access right for two objects (Array of two SetAccessParam objects)
 * // Result: Array with two VersionedObjectResult<Access> objects
 * const access: ObjectCreation = await createAccess([
 *     {
 *         object: objectHash,
 *         person: [personIdHash, anotherPersonIdHash],
 *         hashGroup: [],
 *         mode: Storage.SET_ACCESS_MODE.ADD
 *     },
 *     {
 *         object: objectHash,
 *         person: personIdHash,
 *         hashGroup: [],
 *         mode: Storage.SET_ACCESS_MODE.REPLACE
 *     }
 * ]);
 *
 * @static
 * @async
 * @param {SetAccessParam[]} accessRequests - An array of object/person-references Reference
 * objects. For each object access rights are created for the corresponding Person objects.
 * @returns {Promise<VersionedObjectResult[]>} Returns the result of writing the Access
 * object.
 */
export async function createAccess(accessRequests) {
    return await Promise.all(accessRequests.map((accessRequest) => setAccessForOneObject(accessRequest)));
}
//# sourceMappingURL=access.js.map